import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';

import { api } from '../api/client';
import type { CurrentUser, Role } from '../api/types';
import { Field } from '../components/Field';
import { Loading } from '../components/Loading';
import { useToast } from '../components/Toast';
import { formatDate } from '../format';

export function Users({ user }: { user: CurrentUser }) {
  const queryClient = useQueryClient();
  const { show, element: toastElement } = useToast();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('associate');

  const { data, isLoading } = useQuery({ queryKey: ['users'], queryFn: api.listUsers });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const create = useMutation({
    mutationFn: () => api.createUser({ email, display_name: displayName || undefined, role }),
    onSuccess: async () => {
      await refresh();
      show(`${email} registered as ${role}`);
      setEmail('');
      setDisplayName('');
      setRole('associate');
    },
    onError: (error: unknown) => show(error instanceof Error ? error.message : 'Could not add user', 'error'),
  });

  const update = useMutation({
    mutationFn: (input: { id: number; role?: Role; is_active?: boolean }) =>
      api.updateUser(input.id, { role: input.role, is_active: input.is_active }),
    onSuccess: async () => {
      await refresh();
      show('User updated');
    },
    onError: (error: unknown) => show(error instanceof Error ? error.message : 'Could not update user', 'error'),
  });

  if (isLoading) return <Loading message="Loading users…" />;

  return (
    <div className="page">
      <h1 className="page-title">Users</h1>
      <p className="page-subtitle">
        Registering someone here sets what they can do. Whether they can sign in at all is controlled in
        Entra ID — IT must also add them to the <strong>SG-Complaints-Users</strong> group.
      </p>

      <div className="card">
        <h2 className="section-title">Registered users</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Last sign-in</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((u) => {
                const isSelf = u.email === user.email;
                return (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td className="muted">{u.display_name ?? '—'}</td>
                    <td>
                      <select
                        value={u.role}
                        disabled={isSelf || update.isPending}
                        onChange={(e) => update.mutate({ id: u.id, role: e.target.value as Role })}
                        aria-label={`Role for ${u.email}`}
                      >
                        <option value="associate">Associate</option>
                        <option value="admin">Administrator</option>
                      </select>
                    </td>
                    <td className="faint" style={{ fontSize: 12 }}>
                      {u.last_login_at ? formatDate(u.last_login_at) : 'Never'}
                    </td>
                    <td>
                      {isSelf ? (
                        <span className="badge neutral">You</span>
                      ) : (
                        <button
                          type="button"
                          className={`btn small ${u.is_active ? 'danger' : ''}`}
                          disabled={update.isPending}
                          onClick={() => update.mutate({ id: u.id, is_active: !u.is_active })}
                        >
                          {u.is_active ? 'Deactivate' : 'Reactivate'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2 className="section-title">Add a user</h2>
        <p className="muted" style={{ fontSize: 12, marginBottom: 14 }}>
          <strong>Administrator</strong> — manage users and email configuration, edit any complaint.
          <br />
          <strong>Associate</strong> — lodge complaints, add notes, and edit complaints they lodged.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <div className="grid">
            <Field label="Work email" htmlFor="new-email" required>
              <input
                id="new-email"
                type="email"
                required
                value={email}
                placeholder="name@homepro.com.au"
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field label="Display name" htmlFor="new-name">
              <input id="new-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </Field>
            <Field label="Role" htmlFor="new-role">
              <select id="new-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
                <option value="associate">Associate</option>
                <option value="admin">Administrator</option>
              </select>
            </Field>
          </div>
          <button type="submit" className="btn primary" style={{ marginTop: 14 }} disabled={create.isPending}>
            <UserPlus size={16} aria-hidden /> {create.isPending ? 'Adding…' : 'Add user'}
          </button>
        </form>
      </div>

      {toastElement}
    </div>
  );
}
